import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCoreRowModel, getExpandedRowModel, useReactTable, type ColumnDef } from "@tanstack/react-table";
import { useDeferredValue, useMemo, useState } from "react";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import {
  AD_STATES,
  AD_STATUSES,
  AD_TYPES,
  ALGORITHM_OPTIONS,
  AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES,
  BULK_AD_STATE_ENABLED,
  BULK_AD_STATE_PAUSED,
} from "../../../components/core/blocks/AdPlan/constants";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { BulkActionButton } from "../../../components/table/BulkActionButton";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
} from "../../../components/table/usePersistedTableState";
import { resetTable, selectionStateToSelectedIds } from "../../../components/table/utils";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import AdvertisementExpander from "../../../views/advertising/AdvertisementExpander";
import { RT8FilterWidget } from "../../../views/merchandise/tables/FilterWidget";
import { useBulkUpdateAdState } from "../../advertising/api/useBulkUpdateAdState";
import { DownloadV4Report } from "../DownloadReport";
import { useGetV4AdvReportByAd, type AdvertisementQueryV4Row } from "../api/v4AdvReportByAd";
import { useV4AdvReportExport } from "../api/v4AdvReportExport";
import { useGetV4AdvReportOptions } from "../api/v4AdvReportOptions";
import { EditableTableWrapper, EditableTableSubmitArea } from "../../../components/table/EditableTableWrapper";
import BulkAdPlanActionForm, { type BulkAdPlanActionFormData } from "views/advertising/BulkAdPlanActionForm";
import { BulkDayPartingChart } from "views/advertising/DayPartingChart";
import BulkTagsForm from "components/core/blocks/BulkTagsForm";
import { useBulkAdScheduledActionsUpdate } from "modules/advertising/api/useBulkAdScheduledActionsUpdate";
import { useBulkUpdateDayparting } from "modules/advertising/api/useBulkUpdateDayparting";
import { useBulkUpdateAdPropertiesV2 } from "modules/advertising/api/useBulkUpdateAdPropertiesV2";
import { useAppSelector } from "redux/hooks";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { formatScheduledAction } from "views/advertising/ScheduledBudgetSummary";
import { CategoryField } from "components/core/blocks/AdPlan/fields";
import moment from "moment";
import { formatCurrency } from "utils/formatNumber";
import BulkAdPlanRulesForm from "views/advertising/BulkAdPlanRulesForm";
import { useBulkUpdateAdProperties } from "modules/advertising/api/useBulkUpdateAdProperties";
import { useBulkUpdateAdStateV2 } from "modules/advertising/api/useBulkUpdateAdStateV2";
import { useModuleEnabled } from "redux/selectors/modules";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import BulkAdPlanModifierForm, {
  INITIAL_MODIFIER_FORM_DATA,
  keysToIngoreModifierValidationsFor,
} from "views/advertising/BulkAdPlanModifierForm";
import BulkAdPlanAlgoForm from "views/advertising/BulkAdPlanAlgoForm";
import { type BulkAdPropertyUpdate } from "zodios/apiDefinitions/api/ads";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { NTM_STATUS_OPTIONS, servingStatusOptions } from "../../../views/advertising/adLifecycleFilters";
import { useBiddingPrograms } from "../api/useBiddingPrograms";
import { adTypesByChannel } from "views/advertising/adTypeSection/AdTypeSection";
import { type AdType } from "views/AdvertisingDashboardV2";
import { Select } from "components/fields/base/Select";

type EditableTableFields = {
  daily_budget?: number;
  target_acos?: number;
  max_acos?: number;
  target_roas?: number;
  min_roas?: number;
};

type BulkDaypartingFormData = {
  dayPartingMethod: number;
  day_parting_method?: number;
  day_parting_multipliers?: Record<string, string>;
};

export const getAdTypeFilterOptions = (channel: string): AdType[] => {
  const adTypes = adTypesByChannel[channel as keyof typeof adTypesByChannel];
  return [
    { value: "all", label: "All Media Plan Types" },
    ...adTypes
      .map((adType: AdType) => ({
        value: adType.value,
        label: adType.label,
      }))
      .filter(
        (type: AdType) =>
          ![AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value]
            .map(String)
            .includes(String(type.value)),
      ),
  ];
};

export function AdReportTable({
  state,
  initialState,
  setters,
  columns,
  quickFilterValue,
  setQuickFilterValue,
  additionalFilters,
  adType,
  setAdType,
}: {
  state: ManagedTableState;
  initialState: ManagedTableState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<AdvertisementQueryV4Row, any>[];
  setters: ManagedTableStateSetters;
  quickFilterValue: string;
  setQuickFilterValue: (value: string) => void;
  additionalFilters?: {
    adType?: string;
    categoryId?: string;
  };
  adType?: number | string | null;
  setAdType?: (adType: number | string | null) => void;
}) {
  const [destinationCategoryId, setDestinationCategoryId] = useState<number | null>(null);
  const [filledTags, setFilledTags] = useState<string[]>([]);
  const [bulkActionFormData, setBulkActionFormData] = useState<BulkAdPlanActionFormData>({
    actionUnit: "percent",
    actionType: null,
  });
  const [bulkAlgoFormData, setBulkAlgoFormData] = useState({
    algorithm: ALGORITHM_OPTIONS.ROAS_BOOST.value,
    adType: AD_TYPES.SPONSORED_PRODUCT.value,
    salesVolumeControl: 2,
  });
  const [dayPartingData, setDayPartingData] = useState<BulkDaypartingFormData>({
    dayPartingMethod: 0,
  });
  const [bulkModifierFormData, setBulkModifierFormData] = useState(INITIAL_MODIFIER_FORM_DATA);

  const channel = useAppSelector((state) => state.channel.currentChannel);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const acosOrRoas = useAcosOrRoas();
  const hasNTMModuleEnabled = useModuleEnabled("NON_TRELLIS_MANAGED_ADS");
  const hasBulkActionsModuleEnabled = useModuleEnabled("AD_BULK_ACTIONS");

  const deferredSearch = useDeferredValue(state.globalFilter);
  const quickFilter = useMemo(() => {
    return quickFilterValue !== "all" ? [{ id: quickFilterValue, value: { op: "=", value: true } }] : [];
  }, [quickFilterValue]);
  const filters = useMemo(() => {
    const { adType, categoryId } = additionalFilters || {};
    const adTypeFilter = adType && adType !== "all" ? [{ id: "ad_type", value: { op: "=", value: adType } }] : [];
    const categoryFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
    return [...state.columnFilters, ...adTypeFilter, ...categoryFilter, ...quickFilter];
  }, [state.columnFilters, additionalFilters, quickFilter]);

  const adTypeFilterOptions = getAdTypeFilterOptions(channel);

  const queryClient = useQueryClient();
  const byAdOptionsGetter = useGetV4AdvReportByAd();
  const advByAdReportQuery = useQuery(
    byAdOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters,
      search: deferredSearch,
    }),
  );
  const quickFilterCountsQuery = useQuery(
    // Params here are initial table state. As long as the user didn't change pagination size or sorting, this won't result in a separate request.
    // If they did, this is still static and will only be requested once.
    byAdOptionsGetter({
      pagination: initialState.pagination,
      sorting: initialState.sorting,
      filters:
        additionalFilters?.categoryId ?
          [{ id: "category_id", value: { op: "=", value: additionalFilters.categoryId } }]
        : [],
      search: "",
    }),
  );

  const resetBulkActionsForm = () => {
    setBulkActionFormData({
      actionUnit: "percent",
      actionType: null,
    });
  };

  void queryClient.prefetchQuery({
    ...byAdOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: [...state.columnFilters, ...quickFilter],
      search: deferredSearch,
    }),
    staleTime: 10000,
  });

  const advReportOptionsQuery = useQuery(useGetV4AdvReportOptions());
  const exportMutation = useV4AdvReportExport(state.columnFilters);
  const categoriesQuery = useCategoriesQuery();
  const biddingProgramOptionsQuery = useBiddingPrograms();

  const tagOptions: { value: string; label: string }[] = useMemo((): { value: string; label: string }[] => {
    const tags = advReportOptionsQuery.data?.data.tags ?? [];

    return [
      { value: "all", label: "All Tags" },
      ...tags.map((tag: string): { value: string; label: string } => ({
        value: tag,
        label: tag,
      })),
    ];
  }, [advReportOptionsQuery.data?.data.tags]);

  const biddingProgramOptions: { value: number | string; label: string }[] = useMemo((): {
    value: number | string;
    label: string;
  }[] => {
    const biddingPrograms =
      biddingProgramOptionsQuery.data?.results.map((program) => ({ value: program.id, label: program.name })) ?? [];

    return [{ value: "all", label: "All Rules" }, ...biddingPrograms];
  }, [biddingProgramOptionsQuery.data?.results]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All Categories" },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data],
  );
  const table = useReactTable({
    data: advByAdReportQuery.data?.data ?? [],
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row.ad_id.toString(),
    rowCount: advByAdReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    manualPagination: true,
    meta: {
      showSkeleton: advByAdReportQuery.isFetching && (!advByAdReportQuery.data || advByAdReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        category_id: categoryOptions,
        state: [{ value: "all", label: "All States" }, AD_STATES.ACTIVE, AD_STATES.PAUSE, AD_STATES.ENDED],
        ad_status: [{ value: "all", label: "All Statuses" }, ...Object.values(AD_STATUSES)],
        serving_status: servingStatusOptions,
        day_parting_method: [
          {
            value: "all",
            label: "All",
          },
          {
            value: true,
            label: "With Dayparting",
          },
          {
            value: false,
            label: "Without Dayparting",
          },
        ],
        ntm_enrollment_status: [
          {
            label: "All Statuses",
            value: "all",
          },
          ...NTM_STATUS_OPTIONS,
        ],
        has_active_scheduled_actions: [
          {
            value: true,
            label: "With Scheduled Actions",
          },
          {
            value: false,
            label: "Without Scheduled Actions",
          },
        ],
        program_id: biddingProgramOptions,
        ad_source: [
          {
            label: "All Sources",
            value: "all",
          },
          {
            value: "Trellis",
            label: "Trellis",
          },
          {
            value: "Imported",
            label: "Imported",
          },
        ],
      },
    },
  });

  const updateStateMutation = useBulkUpdateAdState();
  const updateAdPropertiesMutation = useBulkUpdateAdProperties();
  const bulkUpdatePropertiesMutation = useBulkUpdateAdPropertiesV2();
  const updateDaypartingMutation = useBulkUpdateDayparting();
  const updateScheduledActionsMutation = useBulkAdScheduledActionsUpdate();
  const bulkUpdateStateMutation = useBulkUpdateAdStateV2();

  const updatePlanState = (state: "paused" | "active", selected: string[]) => {
    const ads = selected.map((id) => {
      const ad = advByAdReportQuery.data?.data.find((ad) => ad.ad_id.toString() === id);
      return {
        id: parseInt(id),
        name: ad?.ad_name ?? "",
      };
    });

    updateStateMutation.mutate({ ads, state });
  };

  const updatePlanBidModifiers = () => {
    const {
      adType,
      topOfSearchModifier,
      productPagesModifier,
      restOfSearchModifier,
      amazonBusinessModifier,
      sbProductDetailPageModifier,
      sbOtherPageModifier,
      sbHomePageModifier,
      audienceBidMultiplierTargetAudienceId,
      audienceBidMultiplierTargetAudienceName,
      audienceBidMultiplier,
      updatePlacementModifiers,
      updateAudienceModifiers,
    } = bulkModifierFormData;

    const modifierUpdatesToSend = [];

    if (
      [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(adType) &&
      updatePlacementModifiers
    ) {
      const formattedPlacementModifiers = [
        {
          placement: "PLACEMENT_TOP",
          percentage: topOfSearchModifier,
        },
        {
          placement: "PLACEMENT_PRODUCT_PAGE",
          percentage: productPagesModifier,
        },
        {
          placement: "PLACEMENT_REST_OF_SEARCH",
          percentage: restOfSearchModifier,
        },
      ];
      if (AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES.includes(marketPlace.marketPlace)) {
        formattedPlacementModifiers.push({
          placement: "SITE_AMAZON_BUSINESS",
          percentage: amazonBusinessModifier,
        });
      }

      modifierUpdatesToSend.push({
        field: "bid_adjustments_by_placement",
        action: "set",
        value: {
          [adType]: formattedPlacementModifiers,
        },
      } as const);
    }

    if (
      [
        AD_TYPES.SPONSORED_PRODUCT.value,
        AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
        AD_TYPES.SPONSORED_BRAND_V2.value,
      ].includes(adType) &&
      updateAudienceModifiers
    ) {
      const formattedShopperCohortBiddingModifiers =
        audienceBidMultiplierTargetAudienceId ?
          [
            {
              shopperCohortType: "AUDIENCE_SEGMENT",
              percentage: audienceBidMultiplier,
              audienceSegments: [
                {
                  name: audienceBidMultiplierTargetAudienceName,
                  audienceId: audienceBidMultiplierTargetAudienceId.toString(),
                  audienceSegmentType: "SPONSORED_ADS_AMC",
                },
              ],
            },
          ]
        : [];

      modifierUpdatesToSend.push({
        field: "shopper_cohort_bidding",
        action: "set",
        value: {
          [adType]: formattedShopperCohortBiddingModifiers,
        },
      } as const);
    }

    if ([AD_TYPES.SPONSORED_BRAND_V2.value].includes(adType) && updatePlacementModifiers) {
      const formattedSponsoredBrandPlacementModifiers = [
        {
          placement: "HOME",
          percentage: sbHomePageModifier,
        },
        {
          placement: "DETAIL_PAGE",
          percentage: sbProductDetailPageModifier,
        },
        {
          placement: "OTHER",
          percentage: sbOtherPageModifier,
        },
      ];

      modifierUpdatesToSend.push({
        field: "bid_adjustments_by_placement",
        action: "set",
        value: {
          [adType]: formattedSponsoredBrandPlacementModifiers,
        },
      } as const);
    }

    bulkUpdatePropertiesMutation.mutate(
      {
        advertisements: selected.map((id) => parseInt(id)),
        updates: modifierUpdatesToSend,
      },
      {
        onSettled: () => setBulkModifierFormData(INITIAL_MODIFIER_FORM_DATA),
      },
    );
  };

  const scheduleBudgetUpdates = () => {
    for (let i = 0; i < selected.length; i++) {
      const { actionType, actionValue, actionUnit, startDate, endDate } = bulkActionFormData;

      const updateData = formatScheduledAction(
        parseInt(selected[i] as string),
        startDate,
        endDate,
        actionUnit,
        actionValue,
        actionType,
        channel,
      );

      updateScheduledActionsMutation.mutate({
        data: updateData,
        isLast: i === selected.length - 1,
      });
    }
  };

  const selected = selectionStateToSelectedIds(table.getState().rowSelection);
  if (advByAdReportQuery.isError) {
    return <div>Error loading ad report. {import.meta.env.PROD ? null : JSON.stringify(advByAdReportQuery.error)}</div>;
  }

  return (
    <EditableTableWrapper>
      <Wrap justifyContent={"space-between"} width={"100%"} padding={"small"} fontSize={"large"}>
        <ButtonGroup
          buttons={[
            { label: "All Media Plans", value: "all", counter: quickFilterCountsQuery.data?.count },
            {
              label: "New",
              value: "recent",
              counter: quickFilterCountsQuery.data?.count_details.recent_count,
            },
            {
              label: "Trending Up",
              value: "trend_up",
              counter: quickFilterCountsQuery.data?.count_details.trend_up_count,
            },
            {
              label: "Trending Down",
              value: "trend_down",
              counter: quickFilterCountsQuery.data?.count_details.trend_down_count,
            },
            {
              label: acosOrRoas("High ACOS", "Low ROAS"),
              value: "high_acos",
              counter: quickFilterCountsQuery.data?.count_details.high_acos_count,
            },
            {
              label: acosOrRoas("Low ACOS", "High ROAS"),
              value: "low_acos",
              counter: quickFilterCountsQuery.data?.count_details.low_acos_count,
            },
            {
              label: acosOrRoas("ACOS in Range", "ROAS in Range"),
              value: "in_range_acos",
              counter: quickFilterCountsQuery.data?.count_details.in_range_acos_count,
            },
            {
              label: "Low Budget",
              value: "serving_low",
              counter: quickFilterCountsQuery.data?.count_details.serving_low_count,
            },
          ]}
          value={quickFilterValue}
          onSelect={setQuickFilterValue}
        />
        <EditableTableSubmitArea
          onSubmit={async (values: Record<string, EditableTableFields>, form) => {
            const payload = Object.entries(values).map(([id, value]) => {
              return {
                id: parseInt(id),
                ...value,
              };
            });
            await updateAdPropertiesMutation.mutateAsync(payload, {
              onSuccess() {
                form.setInitialValues(form.values);
                void form.reset();
              },
            });
          }}
        />
        <BulkActionButton
          selected={selected}
          options={[
            {
              label: (selected) => `Pause Media Plan${selected.length > 1 ? "s" : ""}`,
              action: (selected) => updatePlanState("paused", selected),
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>Are you sure you want to pause the selected media plans?</p>
              ),
            },
            {
              label: (selected) => `Enable Media Plan${selected.length > 1 ? "s" : ""}`,
              action: (selected) => updatePlanState("active", selected),
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  Are you sure you want to enable the selected media plans?
                </p>
              ),
            },
            {
              label: (selected) => `Pause Media Plan${selected.length > 1 ? "s" : ""} (bulk)`,
              action: (selected) => {
                const updateData = {
                  ad_ids: selected.map((id) => parseInt(id)),
                  state: BULK_AD_STATE_PAUSED as "PAUSED",
                  marketplace: marketPlace.marketPlace,
                };

                bulkUpdateStateMutation.mutate(updateData);
              },
              confirmationMessage: <p>Are you sure you want to pause the selected media plans?</p>,
              hidden: !hasBulkActionsModuleEnabled,
            },
            {
              label: (selected) => `Enable Media Plan${selected.length > 1 ? "s" : ""} (bulk)`,
              action: (selected) => {
                const updateData = {
                  ad_ids: selected.map((id) => parseInt(id)),
                  state: BULK_AD_STATE_ENABLED as "ENABLED",
                  marketplace: marketPlace.marketPlace,
                };

                bulkUpdateStateMutation.mutate(updateData);
              },
              confirmationMessage: <p>Are you sure you want to enable the selected media plans?</p>,
              hidden: !hasBulkActionsModuleEnabled,
            },
            acosOrRoas(
              {
                label: "Update Optimal ACOS Range Low",
                action: () => {
                  if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                    return;
                  }
                  bulkUpdatePropertiesMutation.mutate(
                    {
                      advertisements: selected.map((id) => parseInt(id)),
                      updates: [
                        {
                          field: "target_acos",
                          action:
                            bulkActionFormData.actionType === "specify" ? "set"
                            : bulkActionFormData.actionUnit === "percent" ? "percent"
                            : "fixed",
                          value:
                            bulkActionFormData.actionType === "decrease" ?
                              bulkActionFormData.actionValue * -1
                            : bulkActionFormData.actionValue,
                        },
                      ],
                    },
                    {
                      onSuccess: () => {
                        resetBulkActionsForm();
                      },
                    },
                  );
                },
                confirmationMessage: (
                  <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                    Are you sure you want to update the target ACOS for the selected media plans?
                  </p>
                ),
                actionFormFields: (
                  <BulkAdPlanActionForm
                    formData={bulkActionFormData}
                    setFormData={setBulkActionFormData}
                    actionTarget={"Optimal ACOS Range Low"}
                    hideCurrencyOption={true}
                    forcePercentOption={true}
                  />
                ),
              },
              {
                label: "Update Optimal ROAS Range High",
                action: () => {
                  if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                    return;
                  }
                  bulkUpdatePropertiesMutation.mutate(
                    {
                      advertisements: selected.map((id) => parseInt(id)),
                      updates: [
                        {
                          field: "target_roas",
                          action: bulkActionFormData.actionType === "specify" ? "set" : "fixed",
                          value:
                            bulkActionFormData.actionType === "decrease" ?
                              bulkActionFormData.actionValue * -1
                            : bulkActionFormData.actionValue,
                        },
                      ],
                    },
                    {
                      onSuccess: () => {
                        resetBulkActionsForm();
                      },
                    },
                  );
                },
                confirmationMessage: (
                  <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                    Are you sure you want to update the target ROAS for the selected media plans?
                  </p>
                ),
                actionFormFields: (
                  <BulkAdPlanActionForm
                    formData={bulkActionFormData}
                    setFormData={setBulkActionFormData}
                    actionTarget={"Optimal ROAS Range High"}
                    hideCurrencyOption={true}
                    hidePercentOption={true}
                  />
                ),
              },
            ),
            acosOrRoas(
              {
                label: "Update Optimal ACOS Range High",
                action: () => {
                  if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                    return;
                  }
                  bulkUpdatePropertiesMutation.mutate(
                    {
                      advertisements: selected.map((id) => parseInt(id)),
                      updates: [
                        {
                          field: "max_acos",
                          action:
                            bulkActionFormData.actionType === "specify" ? "set"
                            : bulkActionFormData.actionUnit === "percent" ? "percent"
                            : "fixed",
                          value:
                            bulkActionFormData.actionType === "decrease" ?
                              bulkActionFormData.actionValue * -1
                            : bulkActionFormData.actionValue,
                        },
                      ],
                    },
                    {
                      onSuccess: () => {
                        resetBulkActionsForm();
                      },
                    },
                  );
                },
                confirmationMessage: (
                  <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                    Are you sure you want to update the maximum ACOS for the selected media plans?
                  </p>
                ),
                actionFormFields: (
                  <BulkAdPlanActionForm
                    formData={bulkActionFormData}
                    setFormData={setBulkActionFormData}
                    actionTarget={"Optimal ACOS Range High"}
                    hideCurrencyOption={true}
                    forcePercentOption={true}
                  />
                ),
              },
              {
                label: "Update Optimal ROAS Range Low",
                action: () => {
                  if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                    return;
                  }
                  bulkUpdatePropertiesMutation.mutate(
                    {
                      advertisements: selected.map((id) => parseInt(id)),
                      updates: [
                        {
                          field: "min_roas",
                          action: bulkActionFormData.actionType === "specify" ? "set" : "fixed",
                          value:
                            bulkActionFormData.actionType === "decrease" ?
                              bulkActionFormData.actionValue * -1
                            : bulkActionFormData.actionValue,
                        },
                      ],
                    },
                    {
                      onSuccess: () => {
                        resetBulkActionsForm();
                      },
                    },
                  );
                },
                confirmationMessage: (
                  <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                    Are you sure you want to update the minimum ROAS for the selected media plans?
                  </p>
                ),
                actionFormFields: (
                  <BulkAdPlanActionForm
                    formData={bulkActionFormData}
                    setFormData={setBulkActionFormData}
                    actionTarget={"Optimal ROAS Range Low"}
                    hideCurrencyOption={true}
                    hidePercentOption={true}
                  />
                ),
              },
            ),
            {
              label: "Update Budget",
              action: () => {
                if (!bulkActionFormData.actionType || !bulkActionFormData.actionValue) {
                  return;
                }
                bulkUpdatePropertiesMutation.mutate(
                  {
                    advertisements: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "daily_budget",
                        action:
                          bulkActionFormData.actionType === "specify" ? "set"
                          : bulkActionFormData.actionUnit === "percent" ? "percent"
                          : "fixed",
                        value:
                          bulkActionFormData.actionType === "decrease" ?
                            bulkActionFormData.actionValue * -1
                          : bulkActionFormData.actionValue,
                      },
                    ],
                  },
                  {
                    onSuccess: () => {
                      resetBulkActionsForm();
                    },
                  },
                );
              },
              confirmationMessage: (
                <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                  Are you sure you want to update the daily budget for the selected media plans?
                </p>
              ),
              actionFormFields: (
                <BulkAdPlanActionForm
                  formData={bulkActionFormData}
                  setFormData={setBulkActionFormData}
                  actionTarget={"Daily Budget"}
                  hidePercentOption={bulkActionFormData.actionType === "specify"}
                />
              ),
            },
            {
              label: (selected) => `Update Plan ${selected.length > 1 ? "Categories" : "Category"}`,
              action: () => {
                if (!destinationCategoryId) {
                  return;
                }
                bulkUpdatePropertiesMutation.mutate(
                  {
                    advertisements: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "category",
                        action: "set",
                        value: destinationCategoryId,
                      },
                    ],
                  },
                  {
                    onSuccess: () => {
                      resetBulkActionsForm();
                    },
                  },
                );
              },
              confirmationMessage: (
                <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                  Are you sure you want to update the category for the{" "}
                  {selected.length > 1 ? `${selected.length} selected media plans` : "selected media plan"}?
                </p>
              ),
              actionFormFields: (
                <div className="pb-5">
                  <p className="pb-4 fs-4">
                    Please specify the destination category you would like to move the selected media plan
                    {selected.length > 1 ? "s" : ""} to.
                  </p>
                  <div className="d-flex justify-content-center">
                    <CategoryField
                      dashboard={true}
                      onChange={(value: { categoryId: number }) => {
                        setDestinationCategoryId(value.categoryId);
                      }}
                      state={{
                        updatedCategoryId: destinationCategoryId,
                      }}
                      channel={channel}
                      marketPlace={marketPlace}
                      existingAdCategorySelector={true}
                      fieldsToValidate={[]}
                      errors={{}}
                      inline={false}
                      review={false}
                    />
                  </div>
                </div>
              ),
            },
            {
              label: "Add Media Plan Tags",
              action: () => {
                bulkUpdatePropertiesMutation.mutate(
                  {
                    advertisements: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "tags",
                        action: "add",
                        value: filledTags,
                      },
                    ],
                  },
                  { onSuccess: () => setFilledTags([]) },
                );
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selected}
                  suggestedTags={tagOptions.filter((t) => t.value !== "all").map((tag) => tag.value)}
                  taggedItemDescription={"media plan"}
                  actionType={"add"}
                />
              ),
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  Are you sure you want to add {filledTags.length > 1 ? "these tags" : "this tag"} from the selected
                  media plan
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
              disableConfirmButton: !filledTags.length,
            },
            {
              label: "Remove Media Plan Tags",
              action: () => {
                bulkUpdatePropertiesMutation.mutate(
                  {
                    advertisements: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "tags",
                        action: "remove",
                        value: filledTags,
                      },
                    ],
                  },
                  { onSuccess: () => setFilledTags([]) },
                );
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selected}
                  suggestedTags={tagOptions.filter((t) => t.value !== "all").map((tag) => tag.value)}
                  taggedItemDescription={"media plan"}
                  actionType={"remove"}
                />
              ),
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  Are you sure you want to remove {filledTags.length > 1 ? "these tags" : "this tag"} from the selected
                  media plan
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
              disableConfirmButton: !filledTags.length,
            },
            {
              label: "Schedule Budget Updates",
              action: () => scheduleBudgetUpdates(),
              confirmationMessage: (
                <p className={`fs-4 py-2 ${css({ textAlign: "center" })}`}>
                  {(
                    !bulkActionFormData.actionValue ||
                    !bulkActionFormData.actionType ||
                    !bulkActionFormData.actionUnit ||
                    !bulkActionFormData.startDate ||
                    !bulkActionFormData.endDate
                  ) ?
                    "Please fill out all required fields."
                  : `Are you sure you want to ${bulkActionFormData.actionType} the daily budget by ${
                      bulkActionFormData.actionUnit === "percent" ?
                        `${Math.round(Math.abs(bulkActionFormData.actionValue))}%`
                      : formatCurrency(Math.abs(bulkActionFormData.actionValue), marketPlace.marketPlace)
                    } for the selected media plans from ${moment(bulkActionFormData.startDate).format("MMMM D")} to ${moment(
                      bulkActionFormData.endDate,
                    ).format("MMMM D")}  (${
                      (moment(bulkActionFormData.endDate).diff(bulkActionFormData.startDate, "days") + 1) * 24
                    } hours)?`
                  }
                </p>
              ),

              actionFormFields: (
                <BulkAdPlanActionForm
                  formData={bulkActionFormData}
                  setFormData={setBulkActionFormData}
                  actionTarget={"Daily Budget"}
                  schedulingDialogue={true}
                />
              ),
              disableConfirmButton:
                !bulkActionFormData.actionValue ||
                !bulkActionFormData.actionType ||
                !bulkActionFormData.actionUnit ||
                !bulkActionFormData.startDate ||
                !bulkActionFormData.endDate,
            },
            {
              label: `Cancel Scheduled Budget Updates`,
              action: (selected) => {
                bulkUpdatePropertiesMutation.mutate({
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "cancel_scheduled_actions",
                      action: "set",
                      value: "cancelled",
                    },
                  ],
                });
              },
              confirmationMessage: (
                <p className="fs-4 py-2">
                  Are you sure you want to cancel scheduled budget updates for the selected media plan
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
            },
            {
              label: "Update Dayparting",
              action: () => {
                updateDaypartingMutation.mutate({
                  day_parting_method: dayPartingData.day_parting_method,
                  day_parting_multipliers: dayPartingData.day_parting_multipliers,
                  advertisement_ids: selected.map((id) => parseInt(id)),
                });
              },
              actionFormFields: (
                <BulkDayPartingChart
                  advertisements={selected}
                  onDayPartingChange={setDayPartingData}
                  categoryId={null}
                />
              ),
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  Are you sure you want to update the dayparting for the selected media plan
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
              hidden: channel === "walmart",
            },
            {
              label: "Update Bidding Rules",
              action: () => {
                if (bulkActionFormData.biddingProgram === undefined) {
                  return;
                }
                bulkUpdatePropertiesMutation.mutate({
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "bidding_rules",
                      action: "set",
                      value: bulkActionFormData.biddingProgram,
                    },
                  ],
                });
              },
              actionFormFields: (
                <BulkAdPlanRulesForm formData={bulkActionFormData} setFormData={setBulkActionFormData} />
              ),
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  {bulkActionFormData.biddingProgram === undefined ?
                    ""
                  : `Are you sure you want to update the bidding rules for the selected media plan${selected.length > 1 ? "s" : ""}?`
                  }
                </p>
              ),
              disableConfirmButton: bulkActionFormData.biddingProgram === undefined,
              hidden: channel === "walmart",
            },
            {
              label: "Update Bidding Algorithm",
              action: () => {
                const { algorithm, salesVolumeControl, adType } = bulkAlgoFormData;
                if (!algorithm) {
                  return;
                }

                const algoUpdates = [
                  {
                    field: "bid_algo",
                    action: "set",
                    value: { [adType]: algorithm === ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value ? null : algorithm },
                  },
                ] as BulkAdPropertyUpdate["updates"];

                if (algorithm === ALGORITHM_OPTIONS.GROWTH_MAX.value) {
                  algoUpdates.push({
                    field: "sales_volume_control_slider",
                    action: "set",
                    value: { [adType]: salesVolumeControl },
                  });
                }

                bulkUpdatePropertiesMutation.mutate({
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: algoUpdates,
                });
              },
              actionFormFields: <BulkAdPlanAlgoForm formData={bulkAlgoFormData} setFormData={setBulkAlgoFormData} />,
              confirmationMessage: (
                <p>
                  Are you sure you want to update the bidding algorithm for the selected media plan
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
            },

            {
              label: "Update Bidding Modifiers",
              action: () => updatePlanBidModifiers(),
              confirmationMessage: (
                <p className="fs-4 py-2">
                  Are you sure you want to update the placement modifiers for the selected media plans?
                </p>
              ),
              actionFormFields: (
                <BulkAdPlanModifierForm formData={bulkModifierFormData} setFormData={setBulkModifierFormData} />
              ),
              disableConfirmButton: Object.entries(bulkModifierFormData)
                .filter(([key]) => !keysToIngoreModifierValidationsFor.includes(key))
                .map(([, value]) => value)
                .some((value: number | null) => {
                  return value === null || isNaN(value) || value > 900 || value < 0;
                }),
              hidden: channel === "walmart",
            },
            {
              label: "Import Keywords and Targets",
              action: (selected) => {
                bulkUpdatePropertiesMutation.mutate({
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "non_trellis_enrolled_ad",
                      action: "set",
                      value: true,
                    },
                  ],
                });
              },
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  Are you sure you want to import targets and keywords for the selected media plan
                  {selected.length > 1 ? "s" : ""}? Once they are imported you can enroll this campaign in Trellis to
                  have it automatically managed by our AI.
                </p>
              ),
              hidden: !hasNTMModuleEnabled,
            },
            {
              label: "Manage Imported Media Plans",
              action: (selected) => {
                bulkUpdatePropertiesMutation.mutate({
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "non_trellis_enrolled_ad_accept_bids",
                      action: "set",
                      value: true,
                    },
                  ],
                });
              },
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  Are you sure you want to enroll and automatically accept bids for the selected media plan
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
              hidden: !hasNTMModuleEnabled,
            },
            {
              label: "Unmanage Imported Media Plans",
              action: (selected) => {
                bulkUpdatePropertiesMutation.mutate({
                  advertisements: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "non_trellis_enrolled_ad_accept_bids",
                      action: "set",
                      value: false,
                    },
                  ],
                });
              },
              confirmationMessage: (
                <p className={css({ textAlign: "center" })}>
                  Are you sure you want to disable bid management for the selected media plan
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
              hidden: !hasNTMModuleEnabled,
            },
          ]}
        />
      </Wrap>
      <div className={css({ width: "400px", paddingTop: "large", paddingBottom: "large" })}>
        <Select
          variant={"inline"}
          onSelectionChange={(value) => {
            setAdType?.(value);
          }}
          items={adTypeFilterOptions}
          selectedKey={adType}
        />
      </div>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => {
          return (
            <div className={css({ padding: "medium", backgroundColor: "rgb(239, 239, 239)" })}>
              <ExpanderContainer>
                <AdvertisementExpander adId={row.original.ad_id} />
              </ExpanderContainer>
            </div>
          );
        }}
      />
      <HStack width={"100%"} padding={"small"}>
        <DownloadV4Report downloadMutation={exportMutation} />
      </HStack>
    </EditableTableWrapper>
  );
}
